import { BuilderSection } from 'components/Requests/requests.types';
import { SectionItemTuple } from 'components/Requests/components/RequestsBuilder/components/QuestionsBuilder';
import { DragHandle } from '@mui/icons-material';
import {
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  List,
} from '@mui/material';
import { useDndContext } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { SortableItem } from 'common/Sortable';
import {
  ITEM_DRAG_TYPE,
  getItemSortableContainerId,
  getItemSortableId,
  isItemDropDisabled,
} from '../../question-list-dnd';

export interface QuestionListItemsProps {
  section: BuilderSection;
  sectionIndex: number | null;
  itemIndex: number | null;
  onQuestionClicked: (index: SectionItemTuple) => void;
}

export const QuestionListItems = ({
  section,
  sectionIndex,
  itemIndex,
  onQuestionClicked,
}: QuestionListItemsProps) => {
  const { active } = useDndContext();

  return (
    <SortableContext
      id={getItemSortableContainerId(section.id)}
      key={section.id}
      items={(section?.items2 || []).map((question) =>
        getItemSortableId(question.id, section.sectionIndex),
      )}
      strategy={verticalListSortingStrategy}
    >
      <div>
        <List disablePadding>
          {section?.items2?.map((question, qIdx) => {
            const isInEdit = question.id === 'NEW';
            const isSelectedItem =
              (section.sectionIndex === sectionIndex && qIdx === itemIndex) ||
              isInEdit;

            const label = isInEdit ? 'New Question' : question.prompt || '';
            return (
              <SortableItem
                key={question.id}
                id={getItemSortableId(question.id, section.sectionIndex)}
                data={{ type: ITEM_DRAG_TYPE, sectionId: section.id }}
                role="option"
                disabled={{
                  draggable: isInEdit,
                  droppable: isItemDropDisabled(active, section.id),
                }}
              >
                {({ setHandleNodeRef, attributes, listeners, style }) => {
                  return (
                    <ListItemButton
                      ref={setHandleNodeRef}
                      style={style}
                      {...attributes}
                      {...listeners}
                      onClick={() =>
                        onQuestionClicked([section.sectionIndex ?? 0, qIdx])
                      }
                      selected={isSelectedItem}
                      aria-selected={isSelectedItem}
                      aria-label={label}
                      role="option"
                      key={question.id}
                      sx={{ alignItems: 'flex-start' }}
                    >
                      <ListItemIcon
                        sx={{ minWidth: '36px', marginTop: '.2rem' }}
                      >
                        {!isInEdit && <DragHandle />}
                      </ListItemIcon>
                      <ListItemText>
                        <Box display="flex" gap="10px">
                          <Typography
                            flexGrow={0}
                            sx={{ minWidth: '20px', paddingRight: '0.3rem' }}
                            fontWeight={isSelectedItem ? 700 : 400}
                          >
                            {question.displayPosition !== undefined &&
                              question.displayPosition !== null &&
                              question.displayPosition}
                          </Typography>
                          <Typography
                            textAlign="start"
                            width="100%"
                            fontWeight={isSelectedItem ? 700 : 400}
                          >
                            {label}
                          </Typography>
                        </Box>
                      </ListItemText>
                    </ListItemButton>
                  );
                }}
              </SortableItem>
            );
          })}
        </List>
      </div>
    </SortableContext>
  );
};
